import React, { useState } from 'react';

function ResourcesConfig({ data, onChange }) {
  const [selectedResource, setSelectedResource] = useState(Object.keys(data)[0] || '');
  const [showProviderModal, setShowProviderModal] = useState(false);
  const [newResourceName, setNewResourceName] = useState('');

  const providerTemplates = {
    CSV: {
      type: 'feature',
      name: 'CSV',
      data: 'path/to/data.csv',
      id_field: 'id',
      geometry: { x_field: 'longitude', y_field: 'latitude' }
    },
    GeoJSON: {
      type: 'feature',
      name: 'GeoJSON',
      data: 'path/to/data.geojson',
      id_field: 'id'
    },
    PostgreSQL: {
      type: 'feature',
      name: 'PostgreSQL',
      data: {
        host: 'localhost',
        port: 5432,
        dbname: 'database',
        user: 'username',
        password: 'password'
      },
      id_field: 'id',
      table: 'table_name',
      geom_field: 'geom'
    },
    SQLiteGPKG: {
      type: 'feature',
      name: 'SQLiteGPKG',
      data: 'path/to/data.gpkg',
      id_field: 'fid',
      table: 'layer_name'
    },
    Rasterio: {
      type: 'coverage',
      name: 'rasterio',
      data: 'path/to/raster.tif',
      format: {
        name: 'GTiff',
        mimetype: 'image/tiff'
      }
    }
  };

  const providerCategories = {
    'Feature Providers': ['CSV', 'GeoJSON', 'PostgreSQL', 'SQLiteGPKG'],
    'Coverage Providers': ['Rasterio']
  };

  const handleResourceChange = (resourceName, field, value, nested = null) => {
    const newData = { ...data };
    if (!newData[resourceName]) newData[resourceName] = {};
    
    if (nested) {
      const keys = nested.split('.');
      let current = newData[resourceName];
      for (let i = 0; i < keys.length - 1; i++) {
        if (!current[keys[i]]) current[keys[i]] = {};
        current = current[keys[i]];
      }
      current[keys[keys.length - 1]] = { ...current[keys[keys.length - 1]], [field]: value };
    } else {
      newData[resourceName][field] = value;
    }
    onChange(newData);
  };

  const addResource = () => {
    const name = window.prompt('Enter resource name:');
    if (name && !data[name]) {
      setNewResourceName(name);
      setShowProviderModal(true);
    }
  };

  const createResourceWithProvider = (providerType) => {
    const template = providerTemplates[providerType];
    const newResource = {
      type: 'collection',
      title: newResourceName,
      description: `${newResourceName} collection`,
      keywords: [newResourceName.toLowerCase()],
      providers: [template]
    };
    
    const newData = { ...data, [newResourceName]: newResource };
    onChange(newData);
    setSelectedResource(newResourceName);
    setShowProviderModal(false);
    setNewResourceName('');
  };

  const deleteResource = (resourceName) => {
    if (window.confirm(`Delete resource "${resourceName}"?`)) {
      const newData = { ...data };
      delete newData[resourceName];
      onChange(newData);
      setSelectedResource(Object.keys(newData)[0] || '');
    }
  };

  const resourceNames = Object.keys(data);
  const currentResource = data[selectedResource] || {};
  const currentProvider = currentResource.providers?.[0] || {};

  const renderProviderFields = () => {
    if (!currentProvider.name) return null;

    return (
      <div className="mt-4">
        <div className="card border-0 shadow-sm">
          <div className="card-header bg-success bg-opacity-10 border-0">
            <h6 className="card-title mb-0 text-success">
              <i className="bi bi-gear me-2"></i>
              Provider Configuration
            </h6>
          </div>
          <div className="card-body">
            <div className="row g-3">
              <div className="col-md-6">
                <label className="form-label fw-semibold">Provider Type</label>
                <input type="text" className="form-control" value={currentProvider.name} readOnly />
              </div>
              
              <div className="col-md-6">
                <label className="form-label fw-semibold">Data Source</label>
                {typeof currentProvider.data === 'object' ? (
                  <div className="border rounded p-2">
                    {Object.entries(currentProvider.data).map(([key, value]) => (
                      <div key={key} className="mb-2">
                        <label className="form-label small">{key}</label>
                        <input
                          type={key === 'port' ? 'number' : 'text'}
                          className="form-control form-control-sm"
                          value={value}
                          onChange={(e) => {
                            const providers = [...currentResource.providers];
                            providers[0] = {
                              ...providers[0],
                              data: { ...providers[0].data, [key]: key === 'port' ? parseInt(e.target.value) : e.target.value }
                            };
                            handleResourceChange(selectedResource, 'providers', providers);
                          }}
                        />
                      </div>
                    ))}
                  </div>
                ) : (
                  <input
                    type="text"
                    className="form-control"
                    value={currentProvider.data || ''}
                    onChange={(e) => {
                      const providers = [...currentResource.providers];
                      providers[0] = { ...providers[0], data: e.target.value };
                      handleResourceChange(selectedResource, 'providers', providers);
                    }}
                  />
                )}
              </div>

              {currentProvider.id_field !== undefined && (
                <div className="col-md-6">
                  <label className="form-label fw-semibold">ID Field</label>
                  <input
                    type="text"
                    className="form-control"
                    value={currentProvider.id_field || ''}
                    onChange={(e) => {
                      const providers = [...currentResource.providers];
                      providers[0] = { ...providers[0], id_field: e.target.value };
                      handleResourceChange(selectedResource, 'providers', providers);
                    }}
                  />
                </div>
              )}

              {currentProvider.table !== undefined && (
                <div className="col-md-6">
                  <label className="form-label fw-semibold">Table Name</label>
                  <input
                    type="text"
                    className="form-control"
                    value={currentProvider.table || ''}
                    onChange={(e) => {
                      const providers = [...currentResource.providers];
                      providers[0] = { ...providers[0], table: e.target.value };
                      handleResourceChange(selectedResource, 'providers', providers);
                    }}
                  />
                </div>
              )}

              {currentProvider.geom_field !== undefined && (
                <div className="col-md-6">
                  <label className="form-label fw-semibold">Geometry Field</label>
                  <input
                    type="text"
                    className="form-control"
                    value={currentProvider.geom_field || ''}
                    onChange={(e) => {
                      const providers = [...currentResource.providers];
                      providers[0] = { ...providers[0], geom_field: e.target.value };
                      handleResourceChange(selectedResource, 'providers', providers);
                    }}
                  />
                </div>
              )}

              {currentProvider.geometry && (
                <>
                  <div className="col-md-6">
                    <label className="form-label fw-semibold">X Field (Longitude)</label>
                    <input
                      type="text"
                      className="form-control"
                      value={currentProvider.geometry.x_field || ''}
                      onChange={(e) => {
                        const providers = [...currentResource.providers];
                        providers[0] = {
                          ...providers[0],
                          geometry: { ...providers[0].geometry, x_field: e.target.value }
                        };
                        handleResourceChange(selectedResource, 'providers', providers);
                      }}
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label fw-semibold">Y Field (Latitude)</label>
                    <input
                      type="text"
                      className="form-control"
                      value={currentProvider.geometry.y_field || ''}
                      onChange={(e) => {
                        const providers = [...currentResource.providers];
                        providers[0] = {
                          ...providers[0],
                          geometry: { ...providers[0].geometry, y_field: e.target.value }
                        };
                        handleResourceChange(selectedResource, 'providers', providers);
                      }}
                    />
                  </div>
                </>
              )}

              {currentProvider.format && (
                <>
                  <div className="col-md-6">
                    <label className="form-label fw-semibold">Format Name</label>
                    <input
                      type="text"
                      className="form-control"
                      value={currentProvider.format.name || ''}
                      onChange={(e) => {
                        const providers = [...currentResource.providers];
                        providers[0] = {
                          ...providers[0],
                          format: { ...providers[0].format, name: e.target.value }
                        };
                        handleResourceChange(selectedResource, 'providers', providers);
                      }}
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label fw-semibold">MIME Type</label>
                    <input
                      type="text"
                      className="form-control"
                      value={currentProvider.format.mimetype || ''}
                      onChange={(e) => {
                        const providers = [...currentResource.providers];
                        providers[0] = {
                          ...providers[0],
                          format: { ...providers[0].format, mimetype: e.target.value }
                        };
                        handleResourceChange(selectedResource, 'providers', providers);
                      }}
                    />
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="row g-4">
      <div className="col-lg-4">
        <div className="card border-0 shadow-sm h-100">
          <div className="card-header bg-dark bg-opacity-10 border-0">
            <div className="d-flex justify-content-between align-items-center">
              <h5 className="card-title mb-0">
                <i className="bi bi-collection me-2"></i>
                Resources
              </h5>
              <button 
                className="btn btn-primary btn-sm"
                onClick={addResource}
              >
                <i className="bi bi-plus-lg me-1"></i>
                Add
              </button>
            </div>
          </div>
          <div className="card-body p-0">
            {resourceNames.length === 0 ? (
              <div className="text-center p-4 text-muted">
                <i className="bi bi-inbox display-4 mb-3"></i>
                <p>No resources configured</p>
                <button 
                  className="btn btn-outline-primary"
                  onClick={addResource}
                >
                  Add your first resource
                </button>
              </div>
            ) : (
              <div className="list-group list-group-flush">
                {resourceNames.map(name => (
                  <button
                    key={name}
                    className={`list-group-item list-group-item-action border-0 ${selectedResource === name ? 'active' : ''}`}
                    onClick={() => setSelectedResource(name)}
                  >
                    <div className="d-flex justify-content-between align-items-start">
                      <div className="flex-grow-1">
                        <div className="fw-semibold">{name}</div>
                        <small className={selectedResource === name ? 'text-light' : 'text-muted'}>
                          <i className="bi bi-gear me-1"></i>
                          {data[name]?.providers?.[0]?.name || 'No provider'}
                        </small>
                      </div>
                      <button 
                        className={`btn btn-sm ${selectedResource === name ? 'btn-outline-light' : 'btn-outline-danger'}`}
                        onClick={(e) => { e.stopPropagation(); deleteResource(name); }}
                      >
                        <i className="bi bi-trash"></i>
                      </button>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
      
      <div className="col-lg-8">
        {selectedResource ? (
          <div className="card border-0 shadow-sm">
            <div className="card-header bg-primary bg-opacity-10 border-0">
              <h5 className="card-title mb-0 text-primary">
                <i className="bi bi-pencil-square me-2"></i>
                Edit Resource: {selectedResource}
              </h5>
            </div>
            <div className="card-body">
              <div className="row g-4">
                <div className="col-md-6">
                  <div className="mb-3">
                    <label className="form-label fw-semibold">
                      <i className="bi bi-tag me-1"></i>
                      Type
                    </label>
                    <select 
                      className="form-select form-select-lg" 
                      value={currentResource.type || 'collection'} 
                      onChange={(e) => handleResourceChange(selectedResource, 'type', e.target.value)}
                    >
                      <option value="collection">Collection</option>
                      <option value="process">Process</option>
                    </select>
                  </div>
                  
                  <div className="mb-3">
                    <label className="form-label fw-semibold">
                      <i className="bi bi-card-heading me-1"></i>
                      Title
                    </label>
                    <input 
                      type="text" 
                      className="form-control form-control-lg" 
                      placeholder="Resource title"
                      value={currentResource.title || ''} 
                      onChange={(e) => handleResourceChange(selectedResource, 'title', e.target.value)}
                    />
                  </div>
                  
                  <div className="mb-3">
                    <label className="form-label fw-semibold">
                      <i className="bi bi-card-text me-1"></i>
                      Description
                    </label>
                    <textarea 
                      className="form-control" 
                      rows="4"
                      placeholder="Describe this resource..."
                      value={currentResource.description || ''} 
                      onChange={(e) => handleResourceChange(selectedResource, 'description', e.target.value)}
                    />
                  </div>
                </div>
                
                <div className="col-md-6">
                  <div className="mb-3">
                    <label className="form-label fw-semibold">
                      <i className="bi bi-tags me-1"></i>
                      Keywords
                    </label>
                    <input 
                      type="text" 
                      className="form-control form-control-lg" 
                      placeholder="keyword1, keyword2, keyword3"
                      value={currentResource.keywords?.join(', ') || ''} 
                      onChange={(e) => {
                        const keywords = e.target.value.split(',').map(k => k.trim()).filter(k => k);
                        handleResourceChange(selectedResource, 'keywords', keywords);
                      }}
                    />
                    <div className="form-text">
                      <i className="bi bi-lightbulb me-1"></i>
                      Separate keywords with commas
                    </div>
                  </div>
                  
                  {currentResource.type === 'collection' && (
                    <div className="mb-3">
                      <label className="form-label fw-semibold">
                        <i className="bi bi-bounding-box me-1"></i>
                        Spatial Bounds
                      </label>
                      <input 
                        type="text" 
                        className="form-control form-control-lg" 
                        placeholder="-180,-90,180,90"
                        value={currentResource.extents?.spatial?.bbox?.join(',') || ''} 
                        onChange={(e) => {
                          const bbox = e.target.value.split(',').map(n => parseFloat(n.trim())).filter(n => !isNaN(n));
                          const newData = { ...data };
                          if (!newData[selectedResource].extents) newData[selectedResource].extents = {};
                          if (!newData[selectedResource].extents.spatial) newData[selectedResource].extents.spatial = {};
                          newData[selectedResource].extents.spatial.bbox = bbox;
                          onChange(newData);
                        }}
                      />
                      <div className="form-text">
                        <i className="bi bi-info-circle me-1"></i>
                        Format: minx,miny,maxx,maxy
                      </div>
                    </div>
                  )}
                  
                  {renderProviderFields()}
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="card border-0 shadow-sm">
            <div className="card-body text-center py-5">
              <i className="bi bi-arrow-left display-4 text-muted mb-3"></i>
              <h5 className="text-muted">Select a resource to edit</h5>
              <p className="text-muted">Choose a resource from the list to configure its settings</p>
            </div>
          </div>
        )}
      </div>

      {/* Provider Selection Modal */}
      {showProviderModal && (
        <div className="modal show d-block" style={{backgroundColor: 'rgba(0,0,0,0.5)'}}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className="bi bi-gear me-2"></i>
                  Select Provider Type
                </h5>
                <button className="btn-close" onClick={() => setShowProviderModal(false)}></button>
              </div>
              <div className="modal-body">
                <p>Choose a provider type for resource: <strong>{newResourceName}</strong></p>
                {Object.entries(providerCategories).map(([category, providers]) => (
                  <div key={category} className="mb-4">
                    <h6 className="text-muted">{category}</h6>
                    <div className="row g-2">
                      {providers.map(provider => (
                        <div key={provider} className="col-md-6">
                          <button 
                            className="btn btn-outline-primary w-100 text-start"
                            onClick={() => createResourceWithProvider(provider)}
                          >
                            <div className="fw-bold">{provider}</div>
                            <small className="text-muted d-block">
                              {providerTemplates[provider].type} provider
                            </small>
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ResourcesConfig;